import { Injectable, inject } from '@angular/core';
import { Constants } from '@core/app-constants';
import {
  ApplySubscriptionPricesSuccessResponseDto,
  BasketClient,
  BlockTooltipClient,
  BlockTooltipResponseDto,
  EventSessionPlanContingentImageResponseDto,
  EventSessionPlanFilterRequestDto,
  EventSessionPlanImageResponseDto,
  EventSessionPlanLetterResponseDto,
  EventSessionPlanNumberedBlockFilterResponseDto,
  EventSessionPlanShapeResponseDto,
  OperationResultOfBasketSessionSeatStatusResponseDto,
  OperationResultOfBookUnBookSeatsWithSubscriptionResponseDto,
  OrderSeatClient,
  PlanRequestDto,
  SalesPlanBlockFilterRequestDto,
  SalesPlanBlockFilterResponseDto,
  SalesPlanBlockResponseDto,
  SalesPlanFiltersCategoryResponseDto,
  SalesPlanFiltersContingentResponseDto,
  SalesPlanFiltersPriceResponseDto,
  SalesPlanLegendResponseDto,
  SeatClient,
  SeatPriceApplyingRequestDto,
  SeatsBlockGroupResponseDto,
  SessionSeatsRequestDto,
  StampedVersionSeatsResponseDto,
  SubscriptionPlanBlockDetailsResponseDto,
  SubscriptionPlanClient,
  SubscriptionPlanResponseDto,
  SubscriptionTopViewBackgroundResponseDto,
  TooltipSeatInfoResponseDto,
  TooltipSeatTypeEnumRequestDto,
} from '@core/services/apiclient';
import { HttpHandlerService } from '@core/services/http-handler.service';
import { SalesEventTypeEnum } from '@sales/models/sales.model';
import { EMPTY, Observable, of } from 'rxjs';
import { SalesPlanAbstractService } from './sales-plan-abstract.service';

@Injectable({ providedIn: 'root' })
export class SalesPlanSubscriptionService implements SalesPlanAbstractService {
  private readonly subscriptionPlanService = inject(SubscriptionPlanClient);
  private readonly seatClient = inject(SeatClient);
  private readonly orderSeatClient = inject(OrderSeatClient);
  private readonly basketClient = inject(BasketClient);
  private readonly tooltipClient = inject(BlockTooltipClient);
  private readonly httpHandlerService = inject(HttpHandlerService);

  _type: SalesEventTypeEnum = SalesEventTypeEnum.Subscription;

  public getPlanBackground(
    subscriptionId: number,
  ): Observable<SubscriptionTopViewBackgroundResponseDto | null> {
    return this.subscriptionPlanService
      .getSubscriptionPlanBackground(subscriptionId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionTopViewBackgroundResponseDto>());
  }

  public getTopviewPlanBlocks(
    subscriptionId: number,
    blockId: number,
    filters: PlanRequestDto,
  ): Observable<SubscriptionPlanResponseDto> {
    return this.subscriptionPlanService
      .getSubscriptionPlanBlocks(subscriptionId, blockId, filters, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionPlanResponseDto>());
  }

  public getTopviewPlanOnlyBlocks(
    subscriptionId: number,
    blockId: number,
    filters: PlanRequestDto,
  ): Observable<SalesPlanBlockResponseDto[]> {
    return EMPTY;
  }

  public getBlockData(
    subscriptionId: number,
    blockId: number,
    filters: PlanRequestDto,
  ): Observable<SubscriptionPlanBlockDetailsResponseDto> {
    return this.subscriptionPlanService
      .getPlanBlocksViewDetails(subscriptionId, blockId, filters, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SubscriptionPlanBlockDetailsResponseDto>());
  }

  public getNumberedPlanFilter(
    subscriptionId: number,
    filter: EventSessionPlanFilterRequestDto,
  ): Observable<EventSessionPlanNumberedBlockFilterResponseDto> {
    return of(null);
  }

  public getTootltipSeatInfo(sessionId: number, seatId: number): Observable<TooltipSeatInfoResponseDto> {
    return this.seatClient
      .getTooltipSeatInfo(TooltipSeatTypeEnumRequestDto.Subscription, sessionId, seatId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<TooltipSeatInfoResponseDto>());
  }

  public getBlockDetails(
    channelId: number,
    subscriptionId: number,
    blockId: number,
  ): Observable<BlockTooltipResponseDto> {
    return this.tooltipClient
      .getSubscriptionBlockTooltip(channelId, subscriptionId, blockId, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<BlockTooltipResponseDto>());
  }

  public flagSeats(
    seatIds: number[],
    subscriptionId: number,
    basketId: number,
  ): Observable<OperationResultOfBookUnBookSeatsWithSubscriptionResponseDto> {
    return this.basketClient
      .bookUnBookSubscriptionSeats({ basketId, subscriptionId, seatIds }, Constants.ApiVersion)
      .pipe(
        this.httpHandlerService.handleApiResponse<OperationResultOfBookUnBookSeatsWithSubscriptionResponseDto>(),
      );
  }

  public applyPrices(
    basketId: number,
    subscriptionId: number,
    seatPriceApplyings: SeatPriceApplyingRequestDto[],
  ): Observable<ApplySubscriptionPricesSuccessResponseDto> {
    return this.basketClient
      .applySubscriptionPrices(basketId, subscriptionId, seatPriceApplyings, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<ApplySubscriptionPricesSuccessResponseDto>());
  }

  public getSearchResults(
    subscriptionId: number,
    blockId: number,
    searchTerms: string,
  ): Observable<SeatsBlockGroupResponseDto[]> {
    return this.orderSeatClient
      .getAllSubscriptionSeatsByFilter(subscriptionId, blockId, searchTerms, Constants.ApiVersion)
      .pipe(this.httpHandlerService.handleApiResponse<SeatsBlockGroupResponseDto[]>());
  }

  public flagUnnumberedSeats(
    subscriptionId: number,
    basketId: number,
    sessionSeats: SessionSeatsRequestDto[],
  ): Observable<OperationResultOfBasketSessionSeatStatusResponseDto> {
    return of(null);
  }

  public getLengendInformation(
    channelId: number,
    subscriptionId: number,
  ): Observable<SalesPlanLegendResponseDto> {
    return of(null);
  }

  public getPricesList(
    subscriptionId: number,
    channelId: number,
    contingentIds: number[],
    categoryIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersPriceResponseDto[]> {
    return of(null);
  }

  public getContingentsList(
    subscriptionId: number,
    channelId: number,
    categoryIds: number[],
    priceIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersContingentResponseDto[]> {
    return of(null);
  }

  public getCategoriesList(
    subscriptionId: number,
    channelId: number,
    contingentIds: number[],
    priceIds: number[],
    blockId?: number,
  ): Observable<SalesPlanFiltersCategoryResponseDto[]> {
    return of(null);
  }

  public applyTopviewFilter(
    channelId: number,
    subscriptionId: number,
    filters: SalesPlanBlockFilterRequestDto,
  ): Observable<SalesPlanBlockFilterResponseDto[]> {
    return of(null);
  }

  public getBlockViewSeats(
    salesChannelId: number,
    subscriptionId: number,
    blockId: number,
  ): Observable<StampedVersionSeatsResponseDto> {
    return of(null);
  }

  public getSalesExcludingMultiBlocksSeats(
    salesChannelId: number,
    subscriptionId: number,
  ): Observable<StampedVersionSeatsResponseDto> {
    return of(null);
  }

  public getSalesBlockViewLetters(
    subscriptionId: number,
    blockId: number,
  ): Observable<EventSessionPlanLetterResponseDto[]> {
    return of(null);
  }

  public getSalesBlockViewContingentsImages(
    subscriptionId: number,
    blockId: number,
  ): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return of(null);
  }
  getDifferentialBlockViewSeats(
    salesChannelId: number,
    eventSessionId: number,
    blockId: number,
    stampVersion: string,
  ): Observable<StampedVersionSeatsResponseDto> {
    return of(null);
  }
  getDifferentialSalesExcludingMultiBlocksSeats(
    salesChannelId: number,
    eventSessionId: number,
    stampVersion: string,
  ): Observable<StampedVersionSeatsResponseDto> {
    return of(null);
  }
  public getSalesBlockViewImages(
    subscriptionId: number,
    blockId: number,
  ): Observable<EventSessionPlanImageResponseDto> {
    return of(null);
  }

  public getSalesBlockViewShapes(
    subscriptionId: number,
    blockId: number,
  ): Observable<EventSessionPlanShapeResponseDto[]> {
    return of(null);
  }

  public getSalesExcludingMultiBlocksLetters(
    subscriptionId: number,
  ): Observable<EventSessionPlanLetterResponseDto[]> {
    return of(null);
  }

  public getSalesExcludingMultiBlocksImages(
    subscriptionId: number,
  ): Observable<EventSessionPlanImageResponseDto> {
    return of(null);
  }

  public getSalesExcludingMultiBlocksContingentsImages(
    subscriptionId: number,
  ): Observable<EventSessionPlanContingentImageResponseDto[]> {
    return of(null);
  }

  public getSalesExcludingMultiBlocksShapes(
    subscriptionId: number,
  ): Observable<EventSessionPlanShapeResponseDto[]> {
    return of(null);
  }
}
